import { isValidElement, type ReactNode } from 'react';

import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';

import { MarkdownEntityStaticBadge } from './MarkdownEntityStaticBadge';
import { MeetingSlotBadge } from './MeetingSlotBadge';

// Re-exported from the cycle-free `entitySanitize` module. Sections that
// live in the entity-type registry's import chain (e.g. the meeting
// summary section) MUST import these from `entitySanitize` directly to
// avoid the EntityBadgeById → HoverPreviewBadge → registry → section
// cycle that crashes module init. Other callers (this file's
// `createEntityMarkdownComponents`, MarkdownContent, ProposalListPanel,
// MeetingNotesTab, etc.) are safe to keep importing from here.
export {
  entitySanitizeSchema,
  preprocessEntityTags,
  stripAllMentionFormats,
  stripEntityTags,
} from './entitySanitize';

/**
 * @param entityInteractive When `false`, entity badges render as a static
 * (non-focusable, non-clickable) span instead of the default clickable
 * pill. Defaults to `true`, preserving existing behavior for every current
 * caller. Pass `false` when the markdown is phrasing content nested inside
 * a native `<legend>` or `<label>` — a nested `role="button"` tabbable
 * element there adds a spurious tab stop and corrupts the surrounding
 * control's accessible name.
 * @param resolvedSlots Meeting-slot ids pre-resolved to times (from the
 * rendering action's `enriched_data.resolved_slots`). When a slot id in the
 * content matches a key here, the badge renders immediately with no network
 * request; otherwise it falls back to the async resolve endpoint.
 */
export function createEntityMarkdownComponents(
  orgId?: number,
  linkBuilder?: (id: string, type: string) => string,
  entityInteractive: boolean = true,
  resolvedSlots?: Record<string, { start: string; end: string; label: string }>
) {
  return {
    span: ({
      'data-entity': isEntity,
      'data-meeting-slot': isMeetingSlot,
      type,
      id,
      children,
      ...rest
    }: {
      'data-entity'?: string;
      'data-meeting-slot'?: string;
      type?: string;
      id?: string;
      children?: ReactNode;
      [key: string]: unknown;
    }) => {
      if (!isEntity && !isMeetingSlot) {
        return <span {...rest}>{children}</span>;
      }

      // <meeting-slot id="UUID"/> rendered by the backend IdCodec for meeting_slot
      // type refs. Must NOT go through EntityBadgeById (not a CRM entity). Resolve
      // the slot time via the scheduling API and show a labelled badge.
      if (isMeetingSlot) {
        // Strip "user-content-" prefix that rehype-sanitize may add to ids.
        const rawId = typeof id === 'string' ? id : undefined;
        const strippedSlotId = rawId?.startsWith('user-content-')
          ? rawId.slice('user-content-'.length)
          : rawId;
        // The scheduling resolver keys slots by their canonical lowercase UUID
        // (DRF UUIDField serializes lowercase). Normalize casing so an
        // uppercase ref still resolves instead of falling back to the generic
        // "Meeting time" label.
        const cleanSlotId = strippedSlotId?.toLowerCase();
        if (orgId !== undefined && cleanSlotId) {
          return (
            <MeetingSlotBadge
              slotId={cleanSlotId}
              orgId={orgId}
              preResolved={resolvedSlots?.[cleanSlotId]}
            />
          );
        }
        // No orgId or no id — render a non-interactive fallback that is still
        // visible (satisfies the "neither path may drop the reference" rule).
        return (
          <span className='inline-flex align-middle border-b border-dotted border-[var(--color-border-primary)] text-[var(--color-text-secondary)]'>
            Meeting time
          </span>
        );
      }
      // Extract text from children (may be React elements from remark-gfm auto-linking)
      const extractText = (node: ReactNode): string => {
        if (typeof node === 'string') {
          return node;
        }
        if (typeof node === 'number') {
          return String(node);
        }
        if (Array.isArray(node)) {
          return node.map(extractText).join('');
        }
        if (
          isValidElement<{ children?: ReactNode }>(node) &&
          node.props.children != null
        ) {
          return extractText(node.props.children);
        }
        return '';
      };
      const name = extractText(children) || undefined;
      // Strip "user-content-" prefix from rehype-sanitize
      const cleanId = id?.startsWith('user-content-')
        ? id.slice('user-content-'.length)
        : id;
      const href =
        cleanId && type && linkBuilder ? linkBuilder(cleanId, type) : undefined;

      // Slideout mode: EntityBadgeById handles store hydration + navigation.
      // Require a non-empty `cleanId` — empty IDs would trigger invalid
      // store/fetch lookups in the wrapper.
      if (orgId !== undefined && cleanId && !href) {
        return (
          <EntityBadgeById
            entityId={cleanId}
            orgId={orgId}
            variant='inline'
            interactive={entityInteractive}
            fallbackDisplayName={name}
            fallbackEntityType={type}
          />
        );
      }

      // Static / link mode: render with available data
      const displayName = name ?? type ?? 'Object';
      const badge = (
        <MarkdownEntityStaticBadge
          cleanId={cleanId ?? ''}
          type={type ?? ''}
          displayName={displayName}
        />
      );
      if (href) {
        return (
          <a
            href={href}
            target='_blank'
            rel='noreferrer'
            aria-label={`View ${displayName}`}
            className='inline-flex align-middle'
          >
            {badge}
          </a>
        );
      }
      return badge;
    },
  };
}
